import { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthSession } from "@/contexts/AuthSessionContext";

interface SubscriptionRow {
  is_premium: boolean;
  tier: string | null;
  plan_type: string | null;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  subscription_end: string | null;
}

interface PremiumContextType {
  isPremium: boolean;
  loading: boolean;
  subscription: SubscriptionRow | null;
  refreshPremium: () => Promise<void>;
}

const PremiumContext = createContext<PremiumContextType>({
  isPremium: false,
  loading: true,
  subscription: null,
  refreshPremium: async () => {},
});

export const usePremium = () => useContext(PremiumContext);
export const useSubscription = () => useContext(PremiumContext);

export const PremiumProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoadingAuth } = useAuthSession();
  const [subscription, setSubscription] = useState<SubscriptionRow | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPremiumStatus = useCallback(async () => {
    if (!user?.id) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const queryPromise = supabase
        .from("user_subscriptions")
        .select("is_premium, tier, plan_type, stripe_subscription_id, stripe_customer_id, subscription_end")
        .eq("user_id", user.id)
        .maybeSingle();
      const timeoutPromise = new Promise<{ data: null }>((resolve) =>
        setTimeout(() => resolve({ data: null }), 5000),
      );
      const { data } = (await Promise.race([queryPromise, timeoutPromise])) as { data: SubscriptionRow | null };
      setSubscription(data ?? null);
    } catch {
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Fetch only when auth is resolved and user id changes. No route-change polling.
  useEffect(() => {
    if (isLoadingAuth) return;
    fetchPremiumStatus();
  }, [isLoadingAuth, fetchPremiumStatus]);

  const value = useMemo<PremiumContextType>(() => ({
    isPremium: !!subscription?.is_premium,
    loading,
    subscription,
    refreshPremium: fetchPremiumStatus,
  }), [subscription, loading, fetchPremiumStatus]);

  return <PremiumContext.Provider value={value}>{children}</PremiumContext.Provider>;
};
